import type { ColumnSort } from '@tanstack/react-table';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination } from '~/components/Pagination';
import { useArtistTracksQuery } from '~/modules/artist-roster/hooks/useArtistTracksQuery';
import { useRosterUrlParams } from '~/modules/artist-roster/hooks/useRosterUrlParams';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { DEFAULT_PAGE_SIZE } from '../constants';

interface TrackListHeaderProps {
  uaid: number;
  artistData?: ArtistDetails;
  columnSort?: ColumnSort;
  scrollUpCallback?: () => void;
}

export function TrackListHeader({
  uaid,
  artistData,
  columnSort,
  scrollUpCallback,
}: TrackListHeaderProps) {
  const { pageNumber, setPageNumber } = useRosterUrlParams();
  const { trackEvent } = useAnalytics('Artist Roster');
  const { t } = useTranslation('roster');

  const {
    isLoading: isLoadingTracks,
    data: tracksData,
    error,
  } = useArtistTracksQuery({ uaid, columnSort, pageNumber });

  const totalTracks = tracksData?.pagination.total ?? 0;

  const handleSetPageNumber = useCallback(
    (pageNum: number) => {
      const direction = pageNum > pageNumber ? 'next_page' : 'previous_page';

      trackEvent('artistTrackTablePaginationClick', {
        artist_id: artistData?.partyId ?? '',
        artist_name: artistData?.name ?? '',
        direction,
      });

      setPageNumber(pageNum);
    },
    [
      artistData?.partyId,
      artistData?.name,
      pageNumber,
      setPageNumber,
      trackEvent,
    ],
  );

  useEffect(() => {
    scrollUpCallback?.();
  }, [scrollUpCallback, pageNumber]);

  if (!artistData) {
    return null;
  }

  if (error) {
    return <div>{t('trackList.errors.loadingTracks')}</div>;
  }

  return (
    <div className="my-md inline-flex min-h-xl w-full items-center justify-between">
      <div className="flex gap-xs">
        {t('trackList.header.title')}
        {!isLoadingTracks ? (
          <p className="text-subtle">({totalTracks})</p>
        ) : null}
      </div>
      {totalTracks > DEFAULT_PAGE_SIZE ? (
        <Pagination
          page={pageNumber}
          total={totalTracks}
          perPage={DEFAULT_PAGE_SIZE}
          entity="tracks"
          setPageNumber={handleSetPageNumber}
        />
      ) : null}
    </div>
  );
}

TrackListHeader.displayName = 'TrackListHeader';
